import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../../api/nocode';
import { useAuth } from '../../context/AuthContext';
import { useAcademy } from '../../context/AcademyContext';
import { getCampStatus } from '../../utils/campLogic';
import {
  downloadStudyHallWorkbook,
  downloadStudyHallWorkbookRtf,
  downloadStudyHallWorkbookDocx
} from '../../utils/studyHallWorkbook';
import SafeIcon from '../../common/SafeIcon';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';

const DAYS = [
  { day: 1, title: 'The Christian', subtitle: 'Mindset & Calling' },
  { day: 2, title: 'The Creator', subtitle: 'Writing' },
  { day: 3, title: 'The Caretaker', subtitle: 'Publishing' },
  { day: 4, title: 'The Connector', subtitle: 'Marketing' },
  { day: 5, title: 'Compass Summary', subtitle: 'Review & Export' },
];

function rowsFrom(response) {
  if (Array.isArray(response)) return response;
  return Array.isArray(response?.data) ? response.data : [];
}

function parseJson(value, fallback) {
  if (value && typeof value === 'object') return value;

  try {
    return JSON.parse(value || '');
  } catch {
    return fallback;
  }
}

function isActive(record) {
  return record?.is_active === 1 ||
    record?.is_active === true ||
    record?.is_active === '1';
}

function normalizePrompts(content) {
  const parsed = parseJson(content?.compass_prompts, []);
  const source = Array.isArray(parsed) ? parsed : [];

  const prompts = source.map((item) => {
    if (typeof item === 'string') {
      return {
        title: '',
        description: '',
        prompt: item,
        field_type: 'textarea'
      };
    }

    return {
      title: item?.title ?? '',
      description: item?.description ?? item?.desc ?? '',
      prompt: item?.prompt ?? item?.text ?? '',
      field_type: item?.field_type === 'text' ? 'text' : 'textarea'
    };
  }).filter((item) => item.prompt);

  if (prompts.length === 0 && content?.prompt) {
    prompts.push({
      title: content?.title ?? '',
      description: content?.session_description ?? '',
      prompt: content.prompt,
      field_type: 'textarea'
    });
  }

  return prompts;
}

function PromptField({ prompt, value, onChange, onBlur }) {
  const sharedClass = 'w-full rounded-md border-2 border-[#E87722] bg-[#FDF6EC] p-3 font-poppins focus:outline-none focus:ring-2 focus:ring-[#2D5016]';

  return (
    <div className="rounded-lg border border-[#EDE0CC] bg-white p-5 shadow-sm">
      {prompt.title && (
        <h3 className="text-xl font-bold text-[#2D5016]">{prompt.title}</h3>
      )}
      {prompt.description && (
        <p className="mt-2 leading-relaxed text-gray-600">
          {prompt.description}
        </p>
      )}
      <label className="mt-5 block font-bold text-[#2557AB]">
        {prompt.prompt}
      </label>
      {prompt.field_type === 'text' ? (
        <input
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          className={`${sharedClass} mt-2`}
        />
      ) : (
        <textarea
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          rows={5}
          className={`${sharedClass} mt-2 resize-y`}
        />
      )}
    </div>
  );
}

export default function StudyHall() {
  const { user } = useAuth();
  const { currentRun, currentEnrollment } = useAcademy();
  const [content, setContent] = useState([]);
  const [assemblyMap, setAssemblyMap] = useState({});
  const [entries, setEntries] = useState([]);
  const [answers, setAnswers] = useState({});
  const [activeDay, setActiveDay] = useState(1);
  const [activePanel, setActivePanel] = useState('day');
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [adminBypass, setAdminBypass] = useState(true);
  const [saveStatus, setSaveStatus] = useState('');
  const [exportStatus, setExportStatus] = useState('');
  const location = useLocation();

  useEffect(() => {
    const loadStudyHall = async () => {
      if (!user?.id || !currentRun?.id) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const [contentResponse, assemblyResponse, entryResponse, settingsResponse] =
          await Promise.all([
            api.get('academy_content_gwaacademy', {
              block: 'cabincall'
            }),
            api.get('academy_content_gwaacademy', {
              block: 'assembly'
            }),
            api.get('session_entries_gwaacademy', {
              owner_id: user.id,
              block: 'studyhall'
            }),
            api.get('settings_gwaacademy')
          ]);

        const contentRows = rowsFrom(contentResponse);
        const assemblyRows = rowsFrom(assemblyResponse);
        const entryRows = rowsFrom(entryResponse)
          .sort((a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0));
        const settingsRows = rowsFrom(settingsResponse);
        const loadedAnswers = {};

        entryRows.forEach((entry) => {
          const day = Number(entry?.day ?? 0);
          const entryData = parseJson(entry?.entry_data, {});
          loadedAnswers[day] = {
            ...(loadedAnswers[day] ?? {}),
            ...(entryData?.answers ?? entryData?.responses ?? entryData ?? {})
          };
        });

        const settingsMap = {};
        settingsRows.forEach((setting) => {
          if (setting?.setting_key) {
            settingsMap[setting.setting_key] = setting?.value ?? '';
          }
        });

        const aMap = {};
        assemblyRows.forEach((item) => {
          aMap[item.day] = item;
        });

        setContent(contentRows);
        setAssemblyMap(aMap);
        setEntries(entryRows);
        setAnswers(loadedAnswers);
        setSettings(settingsMap);

        const params = new URLSearchParams(location.search);
        const urlDay = parseInt(params.get('day'));
        if (urlDay && urlDay >= 1 && urlDay <= 5) {
          setActiveDay(urlDay);
          setActivePanel('day');
        } else {
          const firstDay = contentRows
            .map((item) => Number(item?.day ?? 0))
            .filter((day) => day > 0)
            .sort((a, b) => a - b)[0];

          if (firstDay) setActiveDay(firstDay);
        }
      } catch (error) {
        console.error('Failed to load Study Hall', error);
        setSaveStatus(error?.message || 'Unable to load Study Hall.');
      } finally {
        setLoading(false);
      }
    };

    loadStudyHall();
  }, [currentRun?.id, user?.id, location.search]);

  const activeContent = useMemo(() => (
    content.find((item) => Number(item?.day ?? 0) === activeDay) ?? null
  ), [activeDay, content]);

  const prompts = useMemo(
    () => normalizePrompts(activeContent),
    [activeContent]
  );

  const accessUser = {
    ...user,
    pass_type: currentEnrollment?.pass_type ?? user?.pass_type
  };
  const { currentTrailDay, windowOpen } = getCampStatus(
    accessUser,
    settings
  );
  const now = new Date();
  const isContentTimeUnlocked = activeContent?.unlock_time ? (now >= new Date(activeContent.unlock_time)) : (currentTrailDay >= activeDay);
  const dayLocked = (
    !(user?.role === 'admin' && adminBypass) &&
    user?.early_access != 1 &&
    !isContentTimeUnlocked
  );
  const timeLocked = activeContent?.unlock_time &&
    new Date() < new Date(activeContent.unlock_time) &&
    !(user?.role === 'admin' && adminBypass);
  const timeClosed = activeContent?.close_time &&
    new Date() > new Date(activeContent.close_time) &&
    !(user?.role === 'admin' && adminBypass);
  const contentUnavailable = !isActive(activeContent) ||
    dayLocked ||
    timeLocked ||
    timeClosed ||
    (!windowOpen && !(user?.role === 'admin' && adminBypass));

  const updateAnswer = (index, value) => {
    setAnswers((previous) => ({
      ...previous,
      [activeDay]: {
        ...(previous[activeDay] ?? {}),
        [index]: value
      }
    }));
  };

  const saveDay = async () => {
    if (!user?.id || !currentRun?.id || contentUnavailable) return false;

    setSaveStatus('Saving...');

    try {
      // Each user keeps exactly one row per day of prompts: update the most
      // recent matching row and remove any older duplicates for this day.
      const dayEntries = entries
        .filter((entry) => (
          Number(entry?.day ?? 0) === activeDay &&
          entry?.block === 'studyhall'
        ))
        .sort((a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0));
      const existing = dayEntries.length > 0
        ? dayEntries[dayEntries.length - 1]
        : null;
      const duplicates = dayEntries.slice(0, -1);
      const payload = {
        user_id: String(user.id),
        day: activeDay,
        entry_data: JSON.stringify({
          answers: answers[activeDay] ?? {}
        }),
        block: 'studyhall',
        owner_id: String(user.id)
      };

      if (existing?.id) {
        await api.put('session_entries_gwaacademy', existing.id, payload);

        for (const duplicate of duplicates) {
          try {
            await api.delete('session_entries_gwaacademy', duplicate.id);
          } catch (deleteError) {
            console.warn('Failed to remove duplicate Study Hall entry', deleteError);
          }
        }

        setEntries((previous) => previous
          .filter((entry) => !duplicates.some((d) => d?.id === entry?.id))
          .map((entry) => (
            entry?.id === existing.id ? { ...entry, ...payload } : entry
          ))
        );
      } else {
        const response = await api.post(
          'session_entries_gwaacademy',
          payload
        );
        const createdId = response?.data?.id ?? response?.id;

        if (createdId) {
          setEntries((previous) => [
            ...previous,
            { ...payload, id: createdId }
          ]);
        }
      }

      setSaveStatus(`Saved ${new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })}`);
      return true;
    } catch (error) {
      console.error('Study Hall save failed', error);
      setSaveStatus(error?.message || 'Error saving');
      return false;
    }
  };

  const openDay = (day) => {
    setActiveDay(day);
    setActivePanel('day');
  };

  const downloadWorkbook = async (format = 'pdf') => {
    if (!user?.id || !currentRun?.id) return;

    setExportStatus('Preparing your workbook...');

    try {
      if (activePanel === 'day' && activeContent && !contentUnavailable) {
        await saveDay();
      }

      const [contentResponse, entryResponse] = await Promise.all([
        api.get('academy_content_gwaacademy', { block: 'cabincall' }),
        api.get('session_entries_gwaacademy', {
          owner_id: user.id,
          block: 'studyhall'
        })
      ]);
      const latestContent = rowsFrom(contentResponse);
      const latestEntries = rowsFrom(entryResponse);

      setContent(latestContent);
      setEntries(latestEntries);

      if (format === 'docx' || format === 'word') {
        await downloadStudyHallWorkbookDocx({
          currentRun,
          user,
          content: latestContent,
          entries: latestEntries
        });
        setExportStatus('Your Study Hall workbook Word document (.docx) has downloaded.');
      } else if (format === 'rtf') {
        downloadStudyHallWorkbookRtf({
          currentRun,
          user,
          content: latestContent,
          entries: latestEntries
        });
        setExportStatus('Your Study Hall workbook RTF document has downloaded.');
      } else {
        downloadStudyHallWorkbook({
          currentRun,
          user,
          content: latestContent,
          entries: latestEntries
        });
        setExportStatus('Your Study Hall workbook PDF has downloaded.');
      }
    } catch (error) {
      console.error('Study Hall workbook export failed', error);
      setExportStatus(error?.message || 'Unable to create your workbook.');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <SafeIcon
          name="Loader"
          className="text-4xl text-amber animate-spin"
        />
      </div>
    );
  }

  if (!currentRun?.id) {
    return (
      <Card className="mx-auto max-w-3xl py-12 text-center">
        <SafeIcon
          name="Calendar"
          className="mx-auto mb-4 text-4xl text-amber"
        />
        <h1 className="text-2xl font-bold text-dark-green">
          Choose an event run
        </h1>
        <p className="mt-2 text-muted">
          Study Hall prompts and responses are stored separately for each
          Academy event run.
        </p>
      </Card>
    );
  }

  return (
    <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-8 pb-20 animate-fadeIn">
      <div className="w-full lg:w-72 shrink-0 space-y-4 no-print">
        <div className="bg-[#EAF1E6] border-l-4 border-forest-green p-4 rounded-r-md mb-6">
          <h1 className="text-xl font-bold text-dark-green flex items-center gap-2">
            <SafeIcon name="BookOpen" className="text-amber" /> Study Hall
          </h1>
          <p className="text-xs mt-1 text-forest-green font-medium">Academy Prompts</p>
        </div>

        {user?.role === 'admin' && (
          <div className="p-3 bg-dark-green text-white rounded-md flex items-center justify-between text-xs">
            <span className="font-bold flex items-center gap-1">
              <SafeIcon name={adminBypass ? 'Unlock' : 'Lock'} className="text-amber" /> Admin View
            </span>
            <button
              onClick={() => setAdminBypass(!adminBypass)}
              className="bg-white/20 hover:bg-white/30 text-white px-2 py-1 rounded text-[11px] font-bold"
            >
              {adminBypass ? 'Unlocked' : 'Simulate Lock'}
            </button>
          </div>
        )}
        
        <nav className="space-y-2">
          {DAYS.map((d) => {
            const dayContent = content.find((item) => Number(item?.day ?? 0) === d.day);
            const dayTimeUnlocked = dayContent?.unlock_time ? (now >= new Date(dayContent.unlock_time)) : (currentTrailDay >= d.day);
            const unlocked = (user?.role === 'admin' && adminBypass) || user?.early_access == 1 || dayTimeUnlocked;
            const active = activePanel === 'day' && activeDay === d.day;
            const asm = assemblyMap[d.day];
            const personaLabel = asm?.persona_label || dayContent?.persona_label || d.title;
            const dayTitle = asm?.day_title || dayContent?.day_title || dayContent?.section_title || d.subtitle;

            return (
              <button
                key={d.day}
                onClick={() => openDay(d.day)}
                className={`w-full text-left p-4 rounded-md border transition-all flex items-center justify-between
                  ${active ? 'bg-forest-green border-forest-green text-white shadow-md' : 
                    'bg-white border-tan hover:border-amber text-dark-green'}`}
              >
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider mb-1 opacity-80">Day {d.day}</div>
                  <div className={`font-bold ${active ? 'text-white' : 'text-dark-green'}`}>{personaLabel}</div>
                  <div className={`text-xs ${active ? 'text-tan' : 'text-muted'}`}>{dayTitle}</div>
                </div>
                <div>
                  {!unlocked ? (
                    <SafeIcon name="Lock" className="text-gray-400" />
                  ) : active ? (
                    <SafeIcon name="ChevronRight" />
                  ) : (
                    <SafeIcon name="CheckCircle" className="text-amber" />
                  )}
                </div>
              </button>
            );
          })}

          <button
            onClick={() => setActivePanel('export')}
            className={`w-full text-left p-4 rounded-md border transition-all flex items-center justify-between
              ${activePanel === 'export' ? 'bg-[#2557ab] border-[#2557ab] text-white shadow-md' :
                'bg-white border-tan hover:border-amber text-dark-green'}`}
          >
            <div>
              <div className="text-xs font-bold uppercase tracking-wider mb-1 opacity-80">Workbook</div>
              <div className={`font-bold ${activePanel === 'export' ? 'text-white' : 'text-dark-green'}`}>
                Download Your Responses
              </div>
              <div className={`text-xs ${activePanel === 'export' ? 'text-blue-100' : 'text-muted'}`}>
                All five days
              </div>
            </div>
            <SafeIcon name="Download" className={activePanel === 'export' ? 'text-white' : 'text-[#2557ab]'} />
          </button>
        </nav>
      </div>

      <div className="flex-1 min-w-0 space-y-6">
        {activePanel === 'export' ? (
          <Card className="overflow-hidden p-0">
            <div className="bg-[#2557ab] px-6 py-5 text-white">
              <p className="text-xs font-bold uppercase tracking-widest text-blue-100">
                Study Hall Workbook
              </p>
              <h1 className="mt-1 text-3xl font-bold text-white">
                Keep your Academy work together
              </h1>
            </div>
            <div className="space-y-6 p-6 md:p-8">
              <div className="rounded-lg border border-[#C3D8EF] bg-[#F4F8FF] p-5">
                <SafeIcon name="FileText" className="mb-3 text-3xl text-[#2557ab]" />
                <h2 className="text-xl font-bold text-dark-green">
                  Your downloadable workbook
                </h2>
                <p className="mt-2 leading-relaxed text-gray-700">
                  Download a formatted PDF containing every configured Academy
                  Prompt, your saved responses, the selected event run, and all
                  five Study Hall days. You can download a fresh copy anytime.
                </p>
              </div>

              <div className="rounded-lg border border-tan bg-[#FDF6EC] p-5">
                <h3 className="font-bold text-dark-green">Before you download</h3>
                <ul className="mt-3 space-y-2 text-sm text-gray-700">
                  <li>• Use Save Study Hall Responses after editing a prompt answer.</li>
                  <li>• The export fetches your latest stored responses for this event run.</li>
                  <li>• Days without saved responses remain in the workbook for reflection.</li>
                </ul>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <Button onClick={() => downloadWorkbook('pdf')}>
                  <SafeIcon name="Download" />
                  Download Workbook PDF
                </Button>
                <Button onClick={() => downloadWorkbook('docx')}>
                  <SafeIcon name="FileText" />
                  Download Workbook Word
                </Button>
                {exportStatus && (
                  <span className="text-sm font-bold text-forest-green">
                    {exportStatus}
                  </span>
                )}
              </div>
            </div>
          </Card>
        ) : (
          <>
            <header className="border-b-2 border-tan pb-5">
              <p className="text-sm font-bold uppercase tracking-widest text-amber">
                {currentRun?.title || currentRun?.name}
              </p>
              <h1 className="mt-1 text-3xl font-bold text-dark-green">
                Study Hall
              </h1>
              <p className="mt-2 text-muted">
                Work through your Academy Prompts. Your responses are saved only
                to this event run.
              </p>
            </header>

            {!activeContent && (
              <Card className="py-12 text-center">
                <SafeIcon
                  name="BookOpen"
                  className="mx-auto mb-3 text-4xl text-amber"
                />
                <h2 className="text-xl font-bold text-dark-green">
                  No Study Hall prompts yet
                </h2>
                <p className="mt-2 text-muted">
                  Academy Prompts have not been published for this event run.
                </p>
              </Card>
            )}

            {activeContent && contentUnavailable && (
              <Card className="py-12 text-center">
                <SafeIcon
                  name="Lock"
                  className="mx-auto mb-3 text-4xl text-amber"
                />
                <h2 className="text-xl font-bold text-dark-green">
                  Day {activeDay} Study Hall is unavailable
                </h2>
                <p className="mt-2 text-muted">
                  This content has not opened yet or is no longer active.
                </p>
              </Card>
            )}

            {activeContent && !contentUnavailable && (
              <Card className="overflow-hidden p-0">
                <div className="bg-[#2557ab] px-6 py-4 text-white">
                  <p className="text-xs font-bold uppercase tracking-widest text-blue-100">
                    Day {activeDay}
                  </p>
                  <h2 className="text-2xl font-bold text-white">
                    {activeContent?.section_title ||
                      activeContent?.day_title ||
                      activeContent?.title ||
                      'Academy Prompts'}
                  </h2>
                </div>

                <div className="space-y-6 p-6 md:p-8">
                  {(activeContent?.opening_paragraph ||
                    activeContent?.session_description) && (
                    <p className="text-lg leading-relaxed text-gray-700">
                      {activeContent?.opening_paragraph ||
                        activeContent?.session_description}
                    </p>
                  )}

                  {prompts.length === 0 ? (
                    <div className="rounded-md border border-dashed border-gray-300 p-8 text-center text-muted">
                      No Academy Prompts have been added for this day.
                    </div>
                  ) : (
                    prompts.map((prompt, index) => (
                      <PromptField
                        key={`academy-prompt-${activeDay}-${index}`}
                        prompt={prompt}
                        value={answers?.[activeDay]?.[index] ?? ''}
                        onChange={(value) => updateAnswer(index, value)}
                        onBlur={saveDay}
                      />
                    ))
                  )}

                  {prompts.length > 0 && (
                    <div className="flex flex-wrap items-center gap-4 border-t border-tan pt-5">
                      <Button onClick={saveDay}>
                        <SafeIcon name="Save" />
                        Save Study Hall Responses
                      </Button>
                      {saveStatus && (
                        <span className="text-sm font-bold text-forest-green">
                          {saveStatus}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}